import { ApiResponse, PaginationResponse } from '../../../../constants/types';
import { LmsStudentType } from '../../../../constants/types/lms-type';
import { StudentProfileForm } from '../../../../constants/types/student-profile-form';
import instance from '../../../axios/utils.ts/instance';
import { normalizePhone } from '../../../../constants/validation';
import { studentType } from '../../../../constants/types/student-type';
import { getErrorMessages } from '../../util-func';

//===============================================================================================================
// 학생

/**
 * [교사 LMS > 학생 관리] 학생 목록 조회
 * @param classId
 * @param status
 * @param page
 * @param pageSize
 * @returns
 */
export const getStudentList = async (
  classId: string,
  status: string,
  page?: number,
  pageSize?: number,
  classGroupId?: number,
) => {
  try {
    const path = `/v1/lms/${classId}/student`;
    const rtn = await instance.get<
      ApiResponse<PaginationResponse<studentType[]>>
    >(path, { params: { page, pageSize, status, classGroupId } });
    return rtn.data.data;
  } catch (e) {
    console.error(getErrorMessages(e));
  }
};

/**
 * [교사 LMS > 학생 관리] 학생상세조회
 * @param classId
 * @param studentId
 * @returns
 */
export const getStudentDetail = async (classId: string, studentId: number) => {
  const response = await instance.get<ApiResponse<LmsStudentType>>(
    `/v1/lms/${classId}/student/${studentId}`,
  );
  return response.data.data;
};

/** 수정 요청에 보낼 수 있는 항목. 이름/아이디는 수정할 수 없다. */
const EDITABLE_FIELDS = [
  'englishName',
  'birthday',
  'gender',
  'phone',
  'email',
  'address',
  'detailAddress',
  'zipCode',
  'schoolName',
  'schoolGroup',
  'parents',
] as const;

export type StudentField = (typeof EDITABLE_FIELDS)[number];

/** Pick이 폼 타입에 없는 필드를 걸러내므로, EDITABLE_FIELDS가 폼과 어긋나면 여기서 컴파일 에러가 난다. */
export type StudentDetailParams = Partial<
  Pick<StudentProfileForm, StudentField>
>;

/** 조회 응답을 폼 값으로 바꾼다. 폼에서 다루지 않는 필드(id, createdAt 등)는 넘기지 않는다. */
export const toStudentProfileForm = (
  student: LmsStudentType,
): StudentProfileForm => ({
  englishName: student.englishName,
  birthday: student.birthday,
  gender: student.gender,
  phone: student.phone,
  email: student.email,
  address: student.address,
  detailAddress: student.detailAddress,
  zipCode: student.zipCode,
  schoolName: student.schoolName,
  schoolGroup: student.schoolGroup,
  // parents를 생략하거나 null로 내려주면 useFieldArray가 reset을 무시해
  // 이전 목록이 그대로 남는다. 항상 배열로 맞춰서 넘긴다.
  parents: student.parents ?? [],
});

/** 저장 값 정리: 빈 생일은 미입력으로, 연락처는 숫자만 남긴다. */
const normalize = (student: Partial<StudentProfileForm>) => ({
  ...student,
  birthday: student.birthday || undefined,
  parents: (student.parents ?? []).map((parent) => ({
    ...parent,
    phone: normalizePhone(parent.phone ?? ''),
    birthday: parent.birthday || undefined,
  })),
});

/**
 * 조회한 값과 비교해 변경된 항목만 골라낸다.
 * 보호자(parents)는 서버가 배열을 통째로 교체하므로 하나라도 바뀌면 전체를 보낸다.
 * @param fields 비교 대상 필드. 화면의 영역별 저장에 사용한다. (기본값: 수정 가능한 전체 항목)
 */
export const pickChangedStudentFields = (
  student: StudentProfileForm,
  origin?: StudentProfileForm,
  fields: readonly StudentField[] = EDITABLE_FIELDS,
): StudentDetailParams => {
  const next = normalize(student);
  const prev = normalize(origin ?? {});
  const changed: StudentDetailParams = {};

  fields.forEach((field) => {
    // 값 비교로 판단하므로 보호자 추가/삭제, 순서 변경도 함께 잡힌다.
    if (JSON.stringify(next[field]) === JSON.stringify(prev[field])) return;
    Object.assign(changed, { [field]: next[field] });
  });

  return changed;
};

/**
 * [교사 LMS > 학생 관리] 학생정보수정
 * @description
 * - 학생 정보를 수정합니다.
 * - params의 id값은 대상 학생을 식별하는 id이고, 그외 수정할 필드는 전달된 필드만 수정 반영됩니다.
 * - 대상 학생을 식별하는 id는 변경 여부와 관계없이 항상 함께 보낸다.
 * - englishName / gender: 학생 계정에 반영 (user 미연결 학생은 무시)
 * - parents 전달 시 전체 교체됨
 * @param classId
 * @param studentId
 * @param student
 * @returns
 */
export const updateStudentDetail = async (
  classId: string,
  studentId: number,
  params: StudentDetailParams,
) => {
  const response = await instance.put<ApiResponse<LmsStudentType>>(
    `/v1/lms/${classId}/student`,
    { id: studentId, ...params },
  );
  return response.data.data;
};

/**
 * [교사 LMS > 학생 관리] 학생 수강 상태 업데이트 [ 수강생, 대기생, 정지 ]
 * @param classId
 * @param studentIds
 * @param status
 * @returns
 */
export const updateStudentStatus = async (
  classId: string,
  studentIds: number[],
  status: string,
) => {
  const path = `/v1/lms/${classId}/student/status`;
  const rtn = await instance.put(path, { studentIds, status });
  return rtn.data;
};

/**
 * [교사 LMS > 학생 관리] 학생 비밀번호 리셋
 * @param classId
 * @param studentId
 * @returns
 */
export const resetStudentPassword = async (
  classId: string,
  studentId: number,
) => {
  try {
    const result = await instance.put<
      ApiResponse<{ uid: string; password: string }>
    >(`/v1/lms/${classId}/student/reset-password`, { id: studentId });
    return result.data.data;
  } catch (e) {
    console.error(e);
  }
};

/**
 * [교사 LMS] 학생 등록
 * @param classId
 * @param params
 * @returns
 */
export const createStudent = async (
  classId: string,
  params: Partial<LmsStudentType>,
) => {
  const result = await instance.post(`/v1/lms/${classId}/student`, params);
  return result.data;
};

/**
 * [교사 LMS] 학생 uid 중복체크
 * @param params
 * @returns
 */
export const checkStudentUid = async (params: { uid: string }) => {
  const result = await instance.get(`v1/user/uid-check?uid=${params.uid}`);
  return result.data.data as { existUser: boolean };
};

/**
 * [교사 LMS] 학생 삭제
 * @param classId
 * @param ids
 * @returns
 */
export const deleteStudent = async (classId: string, ids: number[]) => {
  try {
    const path = `/v1/lms/${classId}/student`;
    const rtn = await instance.delete(path, { data: { ids } });
    return rtn.data;
  } catch (e) {
    console.error(getErrorMessages(e));
  }
};

//===============================================================================================================
// 학생 반 관리

/**
 * [교사 LMS > 학생 관리] 학생 반 배정
 * @param classId
 * @param params
 * @returns
 */
export const assignStudentToClassGroup = async (
  classId: string,
  params: {
    classGroupId: number;
    studentIds: number[];
  },
) => {
  const result = await instance.put(
    `/v1/lms/${classId}/student/assign-class-group`,
    params,
  );
  return result.data;
};

/**
 * [교사 LMS > 학생 관리] 학생 반 해제
 * @param classId
 * @param studentId
 * @param classGroupId
 * @returns
 */
export const unassignStudentFromClassGroup = async (
  classId: string,
  studentId: number,
  classGroupId: number,
) => {
  const result = await instance.delete(
    `/v1/lms/${classId}/student/${studentId}/assign-class-group/${classGroupId}`,
  );
  return result;
};

//===============================================================================================================

/** 학생 이관 신청 요청 */
export interface StudentTransferRequestParams {
  /** 이관 대상 학생 ID */
  studentId: number;
  /** 이관 신청을 하는 내 LMS 클래스 ID */
  courseId: string;
  /** 이관 신청을 하는 내 LMS 클래스 이름 */
  courseName: string;
  /** 이관 받을 교사 정보 - 아는 만큼 서술 (이름/기관/연락처 등). 실제 대상 교사/클래스는 관리자가 처리 시 확정 */
  toTeacherInfo: string;
  /** 신청 사유 */
  reason: string;
}

/**
 * 학생 이관 신청 상태
 * - PENDING: 처리 대기
 * - APPROVED: 이관 승인
 * - REJECTED: 이관 거절
 * - CANCELED: 취소 (본인이 신청 취소)
 */
export type StudentTransferRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELED';

/** 학생 이관 신청/취소 응답 */
export type StudentTransferRequestResponse = {
  id: number;
  studentId: number;
  status: StudentTransferRequestStatus;
};

/** [고객센터] 학생 이관 신청 */
export const applyStudentTransferRequest = async (
  params: StudentTransferRequestParams,
) => {
  const result = await instance.post(`/v1/cs/student-transfer-request`, params);
  return result.data;
};

/** 학생 이관 신청 결과 */
export type StudentTransferResult = {
  /** 이관 신청 ID */
  id: number;
  /** 이관 대상 학생 ID */
  studentId: number;
  /** 이관 대상 학생 이름 */
  studentName: string;
  /** 이관 대상 학생 로그인 아이디 */
  studentUid: string;
  /** 신청자 ID */
  requesterId: string;
  /** 신청자 이름 */
  requesterName: string;
  /** 신청자 로그인 아이디 */
  requesterUid: string;
  /** 이관 신청을 하는 클래스 ID */
  courseId: string;
  /** 이관 신청을 하는 클래스 이름 */
  courseName: string;
  /** 이관 받을 교사 정보 - 아는 만큼 서술 (이름/기관/연락처 등). 실제 대상 교사/클래스는 관리자가 처리 시 확정 */
  toTeacherInfo: string;
  /** 신청 사유 */
  reason: string;
  /** 신청 상태 */
  status: StudentTransferRequestStatus;
  /** 생성 일시 */
  createdAt: string;
  /** 처리 일시. 처리 전에는 null */
  reviewedAt: string | null;
  /** 관리자 코멘트 (거부 사유 등). 처리 전에는 null */
  reviewComment: string | null;
};

/**
 * [고객센터] 학생 이관 신청 목록 조회
 * @description 목록은 data.result 배열로 내려온다. 화면에서 바로 쓸 수 있도록 배열만 돌려준다.
 */
export const getStudentTransferRequestList = async () => {
  const result = await instance.get<
    ApiResponse<{ result: StudentTransferResult[] }>
  >(`/v1/cs/student-transfer-request`);
  return result.data.data?.result ?? [];
};

/** [고객센터] 학생 이관 신청 취소
 * @description 처리 대기(PENDING) 중인 본인의 이관 신청을 취소합니다.
 * @param id 이관 신청 ID
 * @returns
 * - 400: 이미 처리된 신청 (승인/거부/취소됨)
 * - 404: 본인 신청 중 해당 ID 없음
 */
export const cancelStudentTransferRequest = async (id: number) => {
  const result = await instance.patch<
    ApiResponse<StudentTransferRequestResponse>
  >(`/v1/cs/student-transfer-request/${id}/cancel`);
  return result.data;
};
